export const HALL_BLOCKING_BOOKING_STATUSES = new Set(['tentative', 'confirmed']);

export function normalizeHallTime(value) {
  if (value == null || value === '') return '';
  const match = String(value).trim().match(/^(\d{1,2}):(\d{2})/);
  if (!match) return '';
  return `${String(Number(match[1])).padStart(2, '0')}:${match[2]}`;
}

export function hallTimeToMinutes(value) {
  const normalized = normalizeHallTime(value);
  if (!normalized) return 0;
  const [hours = '0', minutes = '0'] = normalized.split(':');
  return Number(hours) * 60 + Number(minutes);
}

/** Same-day time overlap helper (kept for callers that only have HH:mm). */
export function hallTimesOverlap(startA, endA, startB, endB) {
  const startMinutesA = hallTimeToMinutes(startA);
  const endMinutesA = hallTimeToMinutes(endA);
  const startMinutesB = hallTimeToMinutes(startB);
  const endMinutesB = hallTimeToMinutes(endB);
  if (endMinutesA <= startMinutesA || endMinutesB <= startMinutesB) {
    return false;
  }
  return startMinutesA < endMinutesB && endMinutesA > startMinutesB;
}

export function toHallDateTimeMs(dateValue, timeValue) {
  const dateKey = String(dateValue || '').slice(0, 10);
  const timeKey = normalizeHallTime(timeValue);
  if (!dateKey || !timeKey) return null;
  const ms = Date.parse(`${dateKey}T${timeKey}:00`);
  return Number.isNaN(ms) ? null : ms;
}

export function hallDateTimesOverlap(startA, endA, startB, endB) {
  if (startA == null || endA == null || startB == null || endB == null) {
    return false;
  }
  if (endA <= startA || endB <= startB) {
    return false;
  }
  return startA < endB && endA > startB;
}

export function isHallBlockingBookingStatus(status) {
  return HALL_BLOCKING_BOOKING_STATUSES.has(String(status || '').trim().toLowerCase());
}

export function resolveBookingHallId(booking) {
  if (!booking) return '';
  if (booking.hall_id != null && booking.hall_id !== '') {
    return String(booking.hall_id);
  }
  const hall = booking.hall;
  if (hall == null || hall === '') return '';
  if (typeof hall === 'object') {
    return hall.id != null ? String(hall.id) : '';
  }
  // List API returns numeric FK in `hall`; timeline rows may put the name in `hall`.
  if (/^\d+$/.test(String(hall).trim())) {
    return String(hall).trim();
  }
  return '';
}

export function collectTimelineBookings(timeline = []) {
  const bookings = [];
  const seen = new Set();
  timeline.forEach((slot) => {
    (slot?.events || []).forEach((event) => {
      const key = event?.id ?? `${event?.booking_code}-${event?.start_time}`;
      if (seen.has(key)) return;
      seen.add(key);
      bookings.push(event);
    });
  });
  return bookings;
}

/**
 * Find a blocking booking for the same hall whose datetime range overlaps.
 * Supports same-day hourly and multi-day / overnight (end_date) bookings.
 */
export function findHallBookingConflict({
  bookings = [],
  hallId,
  eventDate,
  endDate = null,
  startTime,
  endTime,
  excludeBookingId = null,
}) {
  if (!hallId || !eventDate || !startTime || !endTime) {
    return null;
  }

  const newStart = toHallDateTimeMs(eventDate, startTime);
  const newEnd = toHallDateTimeMs(endDate || eventDate, endTime);
  if (newStart == null || newEnd == null || newEnd <= newStart) {
    return null;
  }

  const normalizedHallId = String(hallId);
  const excludedId = excludeBookingId != null ? Number(excludeBookingId) : null;

  return bookings.find((booking) => {
    if (excludedId != null && Number(booking?.id) === excludedId) {
      return false;
    }
    if (resolveBookingHallId(booking) !== normalizedHallId) {
      return false;
    }
    if (!isHallBlockingBookingStatus(booking?.status)) {
      return false;
    }

    const bookingStart = toHallDateTimeMs(
      booking?.event_date || booking?.date,
      booking?.start_time,
    );
    const bookingEnd = toHallDateTimeMs(
      booking?.end_date || booking?.event_date || booking?.date,
      booking?.end_time,
    );
    return hallDateTimesOverlap(newStart, newEnd, bookingStart, bookingEnd);
  }) || null;
}

export function formatHallOverlapErrorMessage(conflict, hallName = 'This hall') {
  if (!conflict) {
    return 'This hall already has a booking in that time slot.';
  }
  const eventLabel = conflict.event_name || conflict.customer || 'another event';
  const startDate = String(conflict.event_date || conflict.date || '').slice(0, 10);
  const endDate = String(conflict.end_date || conflict.event_date || conflict.date || '').slice(0, 10);
  const start = normalizeHallTime(conflict.start_time) || '—';
  const end = normalizeHallTime(conflict.end_time) || '—';
  const rangeLabel = startDate && endDate && startDate !== endDate
    ? `${startDate} ${start} → ${endDate} ${end}`
    : `${start} to ${end}`;
  return `"${hallName}" already has "${eventLabel}" booked (${rangeLabel}). Choose another time or a different hall.`;
}

export function findFirstAvailableHall({
  halls = [],
  bookings = [],
  eventDate,
  endDate = null,
  startTime,
  endTime,
  excludeBookingId = null,
  busyHallId = null,
  isHallBookable = () => true,
}) {
  const otherHalls = halls.filter(
    (hall) => isHallBookable(hall) && String(hall.id) !== String(busyHallId || ''),
  );
  const orderedHalls = busyHallId
    ? otherHalls
    : halls.filter((hall) => isHallBookable(hall));

  return orderedHalls.find(
    (hall) => !findHallBookingConflict({
      bookings,
      hallId: hall.id,
      eventDate,
      endDate,
      startTime,
      endTime,
      excludeBookingId,
    }),
  ) || null;
}
